import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  anchorAtPoint,
  caretFromPoint,
  rangeForOffsets,
  ReaderAnchor,
} from '../../lib/reader/selection';

// Once a drag clears this many px it locks to an axis: horizontal → page slide,
// vertical → native scroll. Whichever component is larger wins.
const AXIS_LOCK_SLOP = 12;
// A horizontal drag turns the page if it crosses this fraction of the page width,
// or is a quick flick (this much travel, this fast).
const COMMIT_RATIO = 0.22;
const FLICK_MIN_PX = 50;
const FLICK_MIN_VELOCITY = 0.5; // px per ms
// How long the page-turn / snap-back animation runs, and its easing — a gentle
// ease-in-out so the page glides rather than snapping (which read as a flicker).
const SLIDE_MS = 380;
const SLIDE_EASE = 'cubic-bezier(0.33, 0, 0.2, 1)';
// Trackpad horizontal swipe: accumulate this much deltaX before turning a page,
// and reset the accumulator after this long without a horizontal wheel event.
const WHEEL_TURN_PX = 80;
const WHEEL_IDLE_MS = 160;
const MAX_COL = 672;
const MIN_SIDE = 20;
// Nominal page height (a bit under a screen) used only to estimate the page
// count for the footer. The real page ends are line-snapped at runtime.
const NOMINAL_GUTTER = 48;

export interface PageInfo {
  // 0-based index of the page currently at the top of the viewport.
  page: number;
  pageCount: number;
  // The word at the top of the page — the resolution-independent reading cursor
  // we persist. Null when the top isn't over a paragraph (e.g. a chapter title).
  anchor: ReaderAnchor | null;
}

export interface PagerHandle {
  // Jump one page (`dir` = +1 forward, -1 back), snapped so the top line is never
  // cut. Returns false when already at that edge (caller rolls chapters).
  turnPage(dir: 1 | -1): boolean;
  getAnchor(): ReaderAnchor | null;
  // Scroll so `anchor`'s word sits at the top of the viewport. False if absent.
  scrollToAnchor(anchor: ReaderAnchor): boolean;
  scrollToTop(): void;
  scrollToBottom(): void;
}

export interface PagerBlock {
  key: string;
  node: ReactNode;
}

interface PagerProps {
  blocks: PagerBlock[];
  contentClassName: string;
  // Fires (throttled to one frame) whenever the page/anchor changes — on scroll,
  // on resize, and right after the chapter is laid out.
  onPosition?: (info: PageInfo) => void;
  onSwipeLeft?: () => void; // forward (next page / next chapter)
  onSwipeRight?: () => void; // back (prev page / prev chapter)
  suppressSwipe?: () => boolean;
}

// How far the top line sits from the viewport top (negative when it's clipped
// above it). Used to nudge a page turn so the first line is always whole.
function topLineDelta(vp: HTMLElement): number {
  const rect = vp.getBoundingClientRect();
  const caret = caretFromPoint(rect.left + rect.width / 2, rect.top + 2);
  if (!caret || caret.node.nodeType !== Node.TEXT_NODE) return 0;
  const len = caret.node.textContent?.length ?? 0;
  const r = document.createRange();
  r.setStart(caret.node, Math.min(caret.offset, len));
  r.setEnd(caret.node, Math.min(caret.offset + 1, len));
  const lr = r.getBoundingClientRect();
  return lr.height ? lr.top - rect.top : 0;
}

// The solid mask height that ends a page on a whole line: the blank gap below
// the last fully-visible line, growing to hide a clipped bottom line. Also
// returns that clipped line's viewport-relative top so a forward page turn can
// land it at the top of the next page (so nothing is skipped).
function measureBottom(vp: HTMLElement): { coverHeight: number; cutTop: number | null } {
  const rect = vp.getBoundingClientRect();
  const caret = caretFromPoint(rect.left + rect.width / 2, rect.bottom - 2);
  if (!caret || caret.node.nodeType !== Node.TEXT_NODE) return { coverHeight: 0, cutTop: null };
  const len = caret.node.textContent?.length ?? 0;
  const r = document.createRange();
  r.setStart(caret.node, Math.min(caret.offset, len));
  r.setEnd(caret.node, Math.min(caret.offset + 1, len));
  const lr = r.getBoundingClientRect();
  if (!lr.height) return { coverHeight: 0, cutTop: null };
  // Bottom line fully visible → only the small blank gap below it is masked.
  if (lr.bottom <= rect.bottom + 1)
    return { coverHeight: Math.max(0, rect.bottom - lr.bottom), cutTop: null };
  // Bottom line is clipped → mask it off (clean cut) and report its top.
  return { coverHeight: rect.bottom - lr.top, cutTop: lr.top - rect.top };
}

// The scrollTop one page forward/back from where `vp` sits now, line-snapped so
// the next page starts on a whole line. Returns null when there's no room to
// move that way (the caller rolls into the next/previous chapter). Pure: it
// probes by scrolling and restores the original position synchronously, so no
// paint happens in between.
function pageTarget(vp: HTMLElement, dir: 1 | -1): number | null {
  const max = Math.max(0, vp.scrollHeight - vp.clientHeight);
  const orig = vp.scrollTop;
  if (dir > 0 && orig >= max - 1) return null;
  if (dir < 0 && orig <= 1) return null;
  const m = measureBottom(vp);
  let raw: number;
  if (dir > 0) raw = m.cutTop != null ? orig + m.cutTop : orig + vp.clientHeight;
  else raw = orig - Math.max(1, vp.clientHeight - m.coverHeight);
  raw = Math.max(0, Math.min(max, raw));
  // Pull the (possibly clipped) top line fully into view by probing at `raw`.
  vp.scrollTop = raw;
  const d = topLineDelta(vp);
  const target = d < -1 ? Math.max(0, Math.min(max, raw + d)) : raw;
  vp.scrollTop = orig; // restore — no paint happened in between
  // A forward turn that can't actually advance (last line already shown, but
  // trailing whitespace or the top-line snap keeps us shy of the bottom) is the
  // end of the chapter.
  if (dir > 0 && target <= orig + 1) return null;
  return target;
}

const isDark = () => document.documentElement.classList.contains('dark');
const pageBg = () => (isDark() ? '#111827' /* gray-900 */ : '#ffffff');
const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

// State of an in-flight finger drag. `axis` is undecided until the drag clears
// AXIS_LOCK_SLOP; once horizontal it drives the page slide.
interface Drag {
  startX: number;
  startY: number;
  startT: number;
  axis: 'none' | 'h' | 'v';
  dir: 1 | -1;
  target: number | null; // page scrollTop to land on (null = chapter boundary)
  boundary: boolean;
  clone: HTMLElement | null;
  mover: HTMLElement | null; // the element translated under the finger
  width: number;
  animating: boolean;
}

/**
 * Vertical-scroll reader with a Facebook-reader-style page turn. The whole
 * chapter flows in one scrollable column: a vertical drag scrolls it, and on
 * settle the top line snaps whole (the bottom mask hides the bottom one) so no
 * line is ever cut. A horizontal drag turns the page — the current page slides
 * under the finger with the destination page laid out underneath it, exactly
 * like a physical page being moved aside. Position is reported as a word anchor,
 * so it survives changes of device, font size, or window size.
 */
export const Pager = forwardRef<PagerHandle, PagerProps>(function Pager(
  { blocks, contentClassName, onPosition, onSwipeLeft, onSwipeRight, suppressSwipe },
  ref
) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const onPositionRef = useRef(onPosition);
  const onSwipeLeftRef = useRef(onSwipeLeft);
  const onSwipeRightRef = useRef(onSwipeRight);
  const suppressSwipeRef = useRef(suppressSwipe);
  const rafRef = useRef<number | null>(null);
  const dragRef = useRef<Drag | null>(null);
  // Set up by the gesture effect; lets turnPage() reuse the slide animation.
  const engineRef = useRef<{ runSlide: (dir: 1 | -1) => boolean; busy: () => boolean } | null>(
    null
  );
  // Height of the solid mask that crisply ends the page on a whole line.
  const [coverH, setCoverH] = useState(0);
  useLayoutEffect(() => {
    onPositionRef.current = onPosition;
    onSwipeLeftRef.current = onSwipeLeft;
    onSwipeRightRef.current = onSwipeRight;
    suppressSwipeRef.current = suppressSwipe;
  });

  const readAnchor = useCallback((): ReaderAnchor | null => {
    const vp = viewportRef.current;
    if (!vp) return null;
    const rect = vp.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    for (let dy = 4; dy < 96; dy += 12) {
      const a = anchorAtPoint(x, rect.top + dy);
      if (a) return a;
    }
    return null;
  }, []);

  const report = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const { coverHeight } = measureBottom(vp);
    setCoverH((h) => (Math.abs(h - coverHeight) < 1 ? h : coverHeight));
    const step = Math.max(1, vp.clientHeight - NOMINAL_GUTTER);
    // Count reachable pages from the scrollable range, not the raw content
    // height — trailing whitespace/margins otherwise add a phantom last page
    // the reader can never scroll to (footer would show e.g. 7/8 forever).
    const max = Math.max(0, vp.scrollHeight - vp.clientHeight);
    const pageCount = Math.max(1, Math.round(max / step) + 1);
    const page = Math.min(pageCount - 1, Math.max(0, Math.round(vp.scrollTop / step)));
    onPositionRef.current?.({ page, pageCount, anchor: readAnchor() });
  }, [readAnchor]);

  const scheduleReport = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      report();
    });
  }, [report]);

  // Vertical scrolling is left entirely to the browser — finger-follow with
  // native momentum, settling exactly where you let go (no post-scroll line
  // re-snap, which read as an annoying "balance" wobble). Page ends stay clean:
  // a forward turn lands on a whole line and the bottom mask hides any clipped
  // last line.
  const onScroll = useCallback(() => scheduleReport(), [scheduleReport]);

  useImperativeHandle(
    ref,
    (): PagerHandle => ({
      turnPage(dir) {
        const vp = viewportRef.current;
        if (!vp) return false;
        const target = pageTarget(vp, dir);
        if (target == null) return false; // edge of chapter — caller rolls over
        const engine = engineRef.current;
        if (engine && !prefersReducedMotion()) {
          // Already mid-animation: swallow the input rather than overlap turns.
          if (!engine.busy()) engine.runSlide(dir);
        } else {
          vp.scrollTop = target;
          report();
        }
        return true;
      },
      getAnchor: readAnchor,
      scrollToAnchor(anchor) {
        const vp = viewportRef.current;
        const content = contentRef.current;
        if (!vp || !content) return false;
        const el = content.querySelector<HTMLElement>(
          `[data-reader-seg="${CSS.escape(anchor.segmentId)}"]`
        );
        if (!el) return false;
        const range = rangeForOffsets(el, anchor.charOffset, anchor.charOffset);
        const r = range.getBoundingClientRect();
        const top = r.height || r.width ? r.top : el.getBoundingClientRect().top;
        vp.scrollTop = Math.max(0, vp.scrollTop + (top - vp.getBoundingClientRect().top));
        report();
        return true;
      },
      scrollToTop() {
        if (viewportRef.current) viewportRef.current.scrollTop = 0;
        report();
      },
      scrollToBottom() {
        const vp = viewportRef.current;
        if (vp) vp.scrollTop = vp.scrollHeight;
        report();
      },
    }),
    [readAnchor, report]
  );

  useLayoutEffect(() => {
    const vp = viewportRef.current;
    const content = contentRef.current;
    if (!vp || !content) return undefined;
    report();
    const ro = new ResizeObserver(() => report());
    ro.observe(vp);
    ro.observe(content);
    void document.fonts?.ready.then(() => report());
    return () => {
      ro.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [blocks, report]);

  // --- Facebook-style finger-follow page turn ------------------------------
  // Native listeners (not React's) so touchmove can be non-passive and call
  // preventDefault to keep the viewport from scrolling during a horizontal turn.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return undefined;

    // A static, inert copy of the viewport showing the destination page. It sits
    // either under the moving current page (forward) or on top as the incoming
    // previous page (back). Removed once the turn settles.
    const makeClone = (scrollTop: number): HTMLElement => {
      const c = vp.cloneNode(true) as HTMLElement;
      c.removeAttribute('data-reader-surface');
      c.style.pointerEvents = 'none';
      const parent = vp.parentElement;
      if (parent) parent.insertBefore(c, vp.nextSibling);
      c.scrollTop = scrollTop; // set after insertion so it's laid out
      return c;
    };

    const decorate = (el: HTMLElement, z: number, opaque: boolean) => {
      el.style.zIndex = String(z);
      if (opaque) {
        el.style.background = pageBg();
        el.style.boxShadow = '0 0 28px rgba(0,0,0,0.28)';
      }
    };

    const clearStyles = (el: HTMLElement | null) => {
      if (!el) return;
      el.style.transition = '';
      el.style.transform = '';
      el.style.zIndex = '';
      el.style.background = '';
      el.style.boxShadow = '';
    };

    const endDrag = () => {
      dragRef.current = null;
    };

    const beginHorizontal = (dir: 1 | -1) => {
      const target = pageTarget(vp, dir);
      const boundary = target == null;
      const width = vp.clientWidth;
      const d = dragRef.current;
      if (!d) return;
      d.dir = dir;
      d.target = target;
      d.boundary = boundary;
      d.width = width;
      if (!boundary && target != null) {
        const clone = makeClone(target);
        d.clone = clone;
        if (dir > 0) {
          // Forward: current page (live) slides left off the destination beneath.
          decorate(vp, 2, true);
          decorate(clone, 1, false);
          d.mover = vp;
        } else {
          // Back: previous page (clone) slides in from the left over the current.
          decorate(clone, 2, true);
          decorate(vp, 1, false);
          clone.style.transform = `translateX(${-width}px)`;
          d.mover = clone;
        }
      } else {
        // Chapter boundary: nothing to lay underneath — slide the live page over
        // the backdrop, then let the parent roll chapters on commit.
        decorate(vp, 2, true);
        d.mover = vp;
      }
      if (d.mover) d.mover.style.transition = 'none';
    };

    const moveHorizontal = (dx: number) => {
      const d = dragRef.current;
      if (!d || !d.mover) return;
      const { dir, width } = d;
      if (dir > 0) {
        // Live page tracks left (clamped so it can't overshoot off-screen).
        const tx = Math.max(-width, Math.min(0, dx));
        d.mover.style.transform = `translateX(${tx}px)`;
      } else if (d.boundary) {
        // Boundary back: live page tracks right, revealing the backdrop.
        const tx = Math.max(0, Math.min(width, dx));
        d.mover.style.transform = `translateX(${tx}px)`;
      } else {
        // Incoming previous page slides from -width toward 0 as the finger moves.
        const tx = Math.min(0, Math.max(-width, -width + dx));
        d.mover.style.transform = `translateX(${tx}px)`;
      }
    };

    const settle = (committed: boolean) => {
      const d = dragRef.current;
      if (!d || !d.mover) {
        endDrag();
        return;
      }
      const { dir, width, mover, clone, boundary, target } = d;
      d.animating = true;
      // Where the moving page comes to rest.
      let end: number;
      if (dir > 0)
        end = committed ? -width : 0; // forward: off-screen / back home
      else if (boundary)
        end = committed ? width : 0; // boundary back: off / home
      else end = committed ? 0 : -width; // back: covering / back off-screen

      const finish = (e?: TransitionEvent) => {
        // Ignore transitions bubbling up from child nodes; only ours counts.
        if (e && (e.target !== mover || e.propertyName !== 'transform')) return;
        mover.removeEventListener('transitionend', finish);
        if (committed && boundary) {
          // Roll into the adjacent chapter; the parent remounts the Pager.
          clearStyles(vp);
          if (clone?.parentElement) clone.parentElement.removeChild(clone);
          if (dir > 0) onSwipeLeftRef.current?.();
          else onSwipeRightRef.current?.();
        } else if (committed && target != null) {
          // Land the live viewport on the destination, then drop the clone so
          // the swap is seamless (both layers show the same page at that point).
          vp.scrollTop = target;
          clearStyles(vp);
          if (clone?.parentElement) clone.parentElement.removeChild(clone);
          report();
        } else {
          // Cancelled — restore everything; the live page never moved.
          clearStyles(vp);
          if (clone?.parentElement) clone.parentElement.removeChild(clone);
        }
        endDrag();
      };
      mover.addEventListener('transitionend', finish);
      // Commit the current transform with no transition, then start the real one
      // on the next frame. This is reliable even for the just-inserted clone
      // (the back-direction mover) — a synchronous reflow alone often skips the
      // transition there, so it jumped instead of sliding.
      mover.style.transition = 'none';
      void mover.offsetWidth;
      requestAnimationFrame(() => {
        if (dragRef.current !== d) return;
        mover.style.transition = `transform ${SLIDE_MS}ms ${SLIDE_EASE}`;
        mover.style.transform = `translateX(${end}px)`;
      });
      // Fallback in case transitionend never fires (e.g. zero-distance turn).
      setTimeout(() => {
        if (dragRef.current === d && d.animating) finish();
      }, SLIDE_MS + 140);
    };

    // Programmatic page turn (keyboard arrows, Prev/Next buttons, trackpad
    // swipe) — runs the same slide animation, just without finger-following.
    const runSlide = (dir: 1 | -1): boolean => {
      if (dragRef.current?.animating) return false;
      dragRef.current = {
        startX: 0,
        startY: 0,
        startT: performance.now(),
        axis: 'h',
        dir,
        target: null,
        boundary: false,
        clone: null,
        mover: null,
        width: vp.clientWidth,
        animating: false,
      };
      beginHorizontal(dir);
      const d = dragRef.current;
      if (!d || d.boundary || !d.mover) {
        // No page that way within this chapter — undo and let the caller roll.
        clearStyles(vp);
        if (d?.clone?.parentElement) d.clone.parentElement.removeChild(d.clone);
        dragRef.current = null;
        return false;
      }
      settle(true);
      return true;
    };
    engineRef.current = {
      runSlide,
      busy: () => !!dragRef.current?.animating,
    };

    // Trackpad two-finger horizontal swipe → page turn (and stop the browser
    // from using it for back/forward navigation). Vertical wheel scrolls.
    let wheelAccum = 0;
    let wheelTimer: ReturnType<typeof setTimeout> | null = null;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      if (dragRef.current?.animating || suppressSwipeRef.current?.()) return;
      wheelAccum += e.deltaX;
      if (wheelTimer) clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        wheelAccum = 0;
      }, WHEEL_IDLE_MS);
      if (Math.abs(wheelAccum) < WHEEL_TURN_PX) return;
      const dir: 1 | -1 = wheelAccum > 0 ? 1 : -1; // content shifts left = forward
      wheelAccum = 0;
      if (pageTarget(vp, dir) != null) runSlide(dir);
      else if (dir > 0) onSwipeLeftRef.current?.();
      else onSwipeRightRef.current?.();
    };

    const onTouchStart = (e: TouchEvent) => {
      if (dragRef.current?.animating) return;
      if (e.touches.length !== 1) {
        dragRef.current = null;
        return;
      }
      const t = e.touches[0];
      dragRef.current = {
        startX: t.clientX,
        startY: t.clientY,
        startT: performance.now(),
        axis: 'none',
        dir: 1,
        target: null,
        boundary: false,
        clone: null,
        mover: null,
        width: vp.clientWidth,
        animating: false,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      const d = dragRef.current;
      if (!d || d.animating || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - d.startX;
      const dy = t.clientY - d.startY;
      if (d.axis === 'none') {
        if (Math.abs(dx) < AXIS_LOCK_SLOP && Math.abs(dy) < AXIS_LOCK_SLOP) return;
        if (Math.abs(dx) > Math.abs(dy)) {
          if (suppressSwipeRef.current?.()) {
            d.axis = 'v'; // selection in progress — leave scrolling to the browser
            return;
          }
          d.axis = 'h';
          beginHorizontal(dx < 0 ? 1 : -1);
        } else {
          d.axis = 'v';
          return;
        }
      }
      if (d.axis !== 'h') return;
      e.preventDefault(); // hold the viewport still; we drive the slide
      moveHorizontal(dx);
    };

    const onTouchEnd = (e: TouchEvent) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.axis !== 'h') {
        endDrag();
        return;
      }
      const t = e.changedTouches[0];
      const dx = t ? t.clientX - d.startX : 0;
      const elapsed = performance.now() - d.startT;
      const absdx = Math.abs(dx);
      const matchesDir = d.dir > 0 ? dx < 0 : dx > 0;
      const committed =
        matchesDir &&
        (absdx > d.width * COMMIT_RATIO ||
          (absdx > FLICK_MIN_PX && absdx / Math.max(1, elapsed) > FLICK_MIN_VELOCITY));
      settle(committed);
    };

    vp.addEventListener('touchstart', onTouchStart, { passive: true });
    vp.addEventListener('touchmove', onTouchMove, { passive: false });
    vp.addEventListener('touchend', onTouchEnd, { passive: true });
    vp.addEventListener('touchcancel', onTouchEnd, { passive: true });
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      vp.removeEventListener('touchstart', onTouchStart);
      vp.removeEventListener('touchmove', onTouchMove);
      vp.removeEventListener('touchend', onTouchEnd);
      vp.removeEventListener('touchcancel', onTouchEnd);
      vp.removeEventListener('wheel', onWheel);
      if (wheelTimer) clearTimeout(wheelTimer);
      engineRef.current = null;
      const d = dragRef.current;
      if (d?.clone?.parentElement) d.clone.parentElement.removeChild(d.clone);
      dragRef.current = null;
    };
  }, [report]);

  const side = `clamp(${MIN_SIDE}px, calc((100% - ${MAX_COL}px) / 2), 50%)`;

  return (
    <div className="relative flex-1 overflow-hidden">
      <div
        ref={viewportRef}
        data-reader-surface=""
        // Scrollable (for page turns + select-past-the-page), but the scrollbar
        // is hidden so it just looks like a normal page.
        className="absolute inset-0 overflow-y-auto overscroll-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ touchAction: 'pan-y' }}
        onScroll={onScroll}
      >
        <div
          ref={contentRef}
          className={contentClassName}
          style={{ paddingLeft: side, paddingRight: side }}
        >
          {blocks.map((b) => (
            <div key={b.key} data-key={b.key}>
              {b.node}
            </div>
          ))}
        </div>
      </div>
      {/* Solid mask whose top edge sits just below the last whole line, so the
          page ends in a clean cut with whitespace — not a clipped line. Kept
          above the sliding page layers during a turn. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 bg-white dark:bg-gray-900"
        style={{ height: coverH, zIndex: 3 }}
      />
    </div>
  );
});
